import { forwardRef, Suspense, useState, useCallback, useMemo, CSSProperties } from 'react';
import {
  Box,
  Group,
  Text,
  ActionIcon,
  Menu,
  Skeleton,
  useMantineTheme,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconGripVertical,
  IconChevronDown,
  IconChevronUp,
  IconDots,
  IconRefresh,
  IconSettings,
  IconTrash,
  IconFilter,
  IconMaximize,
  IconMinimize,
} from '@tabler/icons-react';
import { Paper } from '../../../ui';
import { getWidget } from '../registry';
import { useDashboardStore } from '../../../../stores/dashboardStore';
import { useDashboardContext } from '../context/DashboardProvider';
import { WidgetInstance, WidgetProps, DashboardGlobalData, WidgetSettings } from '../types';
import { StatWidgetWrapper } from './StatWidgetWrapper';
import { pickTextColorForBackground } from '../../../../utils/color';
import { usePreferencesStore } from '../../../../stores/preferencesStore';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// Theme Styling Utilities
// ============================================================================

interface ThemeStyles {
  background?: string;
  color?: string;
  border?: string;
  boxShadow?: string;
}

/**
 * Get theme-specific styles based on widget settings
 */
function getThemeStyles(
  settings: WidgetSettings | undefined,
  colorScheme: 'light' | 'dark',
  primaryColor: string
): ThemeStyles {
  const themeVariant = settings?.themeVariant || 'default';

  switch (themeVariant) {
    case 'gradient': {
      // Use primary color for gradient
      const gradientFrom = primaryColor;
      const gradientTo = colorScheme === 'dark' ? '#1a1b1e' : '#f8f9fa';
      return {
        background: `linear-gradient(135deg, ${gradientFrom}22, ${gradientTo})`,
      };
    }
    case 'minimal': {
      return {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
      };
    }
    case 'custom': {
      const bgColor = settings?.backgroundColor;
      const gradientColor = settings?.backgroundGradient;

      if (!bgColor) return {};

      const background = gradientColor
        ? `linear-gradient(135deg, ${bgColor}, ${gradientColor})`
        : bgColor;

      const textColor =
        settings?.textColor === 'auto' ? pickTextColorForBackground(bgColor) : settings?.textColor;

      return {
        background,
        color: textColor,
      };
    }
    default:
      return {};
  }
}

// ============================================================================
// WidgetWrapper Component
// ============================================================================

export interface WidgetWrapperProps {
  /** Widget instance data */
  instance: WidgetInstance;
  /** Global dashboard data */
  globalData?: DashboardGlobalData;
  /** Whether edit mode is active */
  isEditMode?: boolean;
  /** Whether this widget is being dragged */
  isDragging?: boolean;
  /** Drag start handler */
  onDragStart?: (e: React.DragEvent) => void;
  /** Drag end handler */
  onDragEnd?: () => void;
  /** Drag over handler */
  onDragOver?: (e: React.DragEvent) => void;
  /** Drop handler */
  onDrop?: (e: React.DragEvent) => void;
}

export const WidgetWrapper = forwardRef<HTMLDivElement, WidgetWrapperProps>(
  (
    {
      instance,
      globalData,
      isEditMode = false,
      isDragging = false,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDrop,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const widget = getWidget(instance.widgetId);
    const [isHovered, setIsHovered] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    // Get primary color from preferences store
    const colorSchemeData = usePreferencesStore((state) => state.getColorScheme());
    const primaryColor = colorSchemeData?.primary?.from || theme.primaryColor;
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Dashboard context for data refresh
    const dashboardContext = useDashboardContext();

    // Get theme-specific styles
    const themeStyles = useMemo(
      () => getThemeStyles(instance.settings, colorScheme, primaryColor),
      [instance.settings, colorScheme, primaryColor]
    );

    // Store actions
    const toggleWidgetCollapsed = useDashboardStore((state) => state.toggleWidgetCollapsed);
    const removeWidget = useDashboardStore((state) => state.removeWidget);
    const setWidgetFilters = useDashboardStore((state) => state.setWidgetFilters);
    const openSettingsModal = useDashboardStore((state) => state.openSettingsModal);
    const openFiltersModal = useDashboardStore((state) => state.openFiltersModal);
    const expandWidget = useDashboardStore((state) => state.expandWidget);
    const collapseExpandedWidget = useDashboardStore((state) => state.collapseExpandedWidget);
    const expandedWidget = useDashboardStore((state) => state.expandedWidget);

    const isExpanded = expandedWidget === instance.instanceId;

    // Refresh handler - triggers actual data refetch
    const handleRefresh = useCallback(() => {
      // Increment local refresh key to re-render widget
      setRefreshKey((prev) => prev + 1);
      // Also trigger global data refetch
      dashboardContext.refetch();
    }, [dashboardContext]);

    // Filters handler
    const handleFilters = useCallback(() => {
      openFiltersModal(instance.instanceId);
    }, [openFiltersModal, instance.instanceId]);

    // Expand handler
    const handleExpand = useCallback(() => {
      if (isExpanded) {
        collapseExpandedWidget();
      } else {
        expandWidget(instance.instanceId);
      }
    }, [isExpanded, expandWidget, collapseExpandedWidget, instance.instanceId]);

    // Settings handler
    const handleSettings = useCallback(() => {
      openSettingsModal(instance.instanceId);
    }, [openSettingsModal, instance.instanceId]);

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver?.(e);
      },
      [onDragOver]
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDrop?.(e);
      },
      [onDrop]
    );

    // Widget not found in registry
    if (!widget) {
      return (
        <Paper ref={ref} p="md" withBorder shadow="sm" style={{ height: '100%' }}>
          <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Text c="dimmed" size="sm" style={accessibilityStyles}>Widget not found</Text>
            <Text c="dimmed" size="xs" style={accessibilityStyles}>{instance.widgetId}</Text>
            {isEditMode && (
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                mt="sm"
                onClick={() => removeWidget(instance.instanceId)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            )}
          </Box>
        </Paper>
      );
    }

    // Check if widget should use headerless stat wrapper
    const isStatWidget = widget.displayStyle === 'stat' || widget.hasHeader === false;

    if (isStatWidget) {
      return (
        <StatWidgetWrapper
          ref={ref}
          instance={instance}
          globalData={globalData}
          isEditMode={isEditMode}
          isDragging={isDragging}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
          onDrop={onDrop}
        />
      );
    }

    const WidgetComponent = widget.component;
    const Icon = widget.icon;

    const widgetProps: WidgetProps = {
      widgetId: instance.widgetId,
      instanceId: instance.instanceId,
      isCollapsed: instance.isCollapsed,
      globalData,
      filters: instance.filters,
      onFilterChange: (filters) => setWidgetFilters(instance.instanceId, filters),
      settings: instance.settings,
    };

    // Build combined styles with theme overrides
    const paperStyle: CSSProperties = {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      cursor: isEditMode ? 'grab' : 'default',
      transition: 'box-shadow 0.2s ease, opacity 0.2s ease, transform 0.2s ease',
      opacity: isDragging ? 0.5 : 1,
      transform: isDragging ? 'scale(1.02)' : 'scale(1)',
      overflow: 'hidden',
      // Apply theme styles
      ...(themeStyles.background && { background: themeStyles.background }),
      ...(themeStyles.color && { color: themeStyles.color }),
      ...(themeStyles.border && { border: themeStyles.border }),
      ...(themeStyles.boxShadow !== undefined && { boxShadow: themeStyles.boxShadow }),
    };

    // Determine if border should be shown (not for minimal theme)
    const showBorder = instance.settings?.themeVariant !== 'minimal';
    const showShadow = instance.settings?.themeVariant !== 'minimal';

    return (
      <Paper
        ref={ref}
        p={0}
        withBorder={showBorder}
        shadow={showShadow ? 'sm' : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        draggable={isEditMode}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={paperStyle}
      >
            {/* Widget Header */}
            <WidgetHeader
              widget={widget}
              instance={instance}
              isEditMode={isEditMode}
              isHovered={isHovered}
              isExpanded={isExpanded}
              onToggleCollapse={() => toggleWidgetCollapsed(instance.instanceId)}
              onRefresh={handleRefresh}
              onFilters={handleFilters}
              onExpand={handleExpand}
              onSettings={handleSettings}
              onRemove={() => removeWidget(instance.instanceId)}
            />

            {/* Widget Content */}
            {instance.isCollapsed ? null : (
              <Box
                p="sm"
                pb="md"
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Suspense fallback={<WidgetLoader />}>
                  <WidgetComponent key={refreshKey} {...widgetProps} />
                </Suspense>
              </Box>
            )}
      </Paper>
    );
  }
);

WidgetWrapper.displayName = 'WidgetWrapper';

// ============================================================================
// WidgetHeader Component
// ============================================================================

interface WidgetHeaderProps {
  widget: NonNullable<ReturnType<typeof getWidget>>;
  instance: WidgetInstance;
  isEditMode: boolean;
  isHovered: boolean;
  isExpanded: boolean;
  onToggleCollapse: () => void;
  onRefresh: () => void;
  onFilters: () => void;
  onExpand: () => void;
  onSettings: () => void;
  onRemove: () => void;
}

function WidgetHeader({
  widget,
  instance,
  isEditMode,
  isHovered,
  isExpanded,
  onToggleCollapse,
  onRefresh,
  onFilters,
  onExpand,
  onSettings,
  onRemove,
}: WidgetHeaderProps) {
  const theme = useMantineTheme();
  const Icon = widget.icon;
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Check if widget supports these features
  const hasFilters = widget.hasFilters || (widget.data.sourceType !== 'global' && widget.filterLocation !== 'inline');
  const hasSettings = widget.hasSettings !== false;
  const canExpand = widget.expandable !== false;

  return (
    <Box
      px="sm"
      py="xs"
      style={{
        borderBottom: `1px solid var(--mantine-color-default-border)`,
        backgroundColor: isEditMode ? 'var(--mantine-color-gray-light)' : 'transparent',
        flexShrink: 0,
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs">
        {/* Left side: drag handle + icon + title */}
        <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {isEditMode && (
            <Box style={{ cursor: 'grab', flexShrink: 0 }}>
              <IconGripVertical size={16} color={theme.colors.gray[5]} />
            </Box>
          )}
          <Box style={{ flexShrink: 0 }}>
            <Icon size={18} />
          </Box>
          <Text fw={600} size="sm" lineClamp={1} style={{ ...accessibilityStyles, flex: 1, minWidth: 0 }}>
            {instance.settings?.customTitle || widget.name}
          </Text>
        </Group>

        {/* Right side: actions */}
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          {/* Collapse/Expand button */}
          {widget.collapsible !== false && (
            <Tooltip label={instance.isCollapsed ? 'Expand' : 'Collapse'} withArrow>
              <ActionIcon size="sm" variant="subtle" onClick={onToggleCollapse}>
                {instance.isCollapsed ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronUp size={16} />
                )}
              </ActionIcon>
            </Tooltip>
          )}

          {/* Menu (visible on hover or in edit mode) */}
          {(isHovered || isEditMode) && (
            <Menu position="bottom-end" withinPortal withArrow>
              <Menu.Target>
                <ActionIcon size="sm" variant="subtle">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconRefresh size={14} />} onClick={onRefresh}>
                  Refresh Data
                </Menu.Item>
                {hasFilters && (
                  <Menu.Item leftSection={<IconFilter size={14} />} onClick={onFilters}>
                    Filters
                  </Menu.Item>
                )}
                {canExpand && (
                  <Menu.Item
                    leftSection={isExpanded ? <IconMinimize size={14} /> : <IconMaximize size={14} />}
                    onClick={onExpand}
                  >
                    {isExpanded ? 'Minimize' : 'Expand'}
                  </Menu.Item>
                )}
                {hasSettings && (
                  <Menu.Item leftSection={<IconSettings size={14} />} onClick={onSettings}>
                    Settings
                  </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconTrash size={14} />}
                  color="red"
                  onClick={onRemove}
                >
                  Remove
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>
    </Box>
  );
}

// ============================================================================
// WidgetLoader Component
// ============================================================================

export function WidgetLoader() {
  return (
    <Box>
      <Skeleton height={20} width="60%" mb="sm" />
      <Skeleton height={80} mb="sm" />
      <Skeleton height={20} width="80%" />
    </Box>
  );
}

export default WidgetWrapper;
